import {
  BaseProductsClient,
  Parhato2ProductSubscription,
  Product,
  ProductResults,
  ProductReviewPayload,
  ProductsQueryParams,
  SearchProductsQueryParams,
} from "@core/types";
import { getCurrentExpressPointId } from "./utils";
import { supabaseClient } from "../supabase";
import { Tables } from "../types/supabase";
import { convertFortimeProduct, convertSupaReview } from "./converters";
import { FortimeProduct } from "./types";
import { SupaProduct } from "../types/supabaseFragments";

const DEFAULT_PRODUCTS_PARAMS = {
  status: "publish",
  stock_status: "instock",
  min_price: 0.1,
  per_page: 21,
};

function convertProductsProps(props: ProductsQueryParams) {
  return {
    on_sale: props.saleType,
    page: props.pageNumber,
    per_page: props.pageSize,
    category: props.categoryId,
    orderby: props.sortOrder,
    min_price: props.minPrice,
    max_price: props.maxPrice,
    keyword: props.keyword,
    featured: props.featured,
  };
}

class ProductsClient extends BaseProductsClient {
  async getProducts(props: ProductsQueryParams): Promise<ProductResults> {
    const response = await this.instance.get<SupaProduct[]>("/wp-json/wc/v3/products?", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        ...convertProductsProps(props),
        shippingZone: props.zoneId,
        expressPointId: getCurrentExpressPointId(),
        type: ["variable", "simple"].join(","),
      },
    });

    return {
      additionalFeatures: [],
      products: response.data.map((product) => convertFortimeProduct(product as any as FortimeProduct)),
    };
  }

  async searchProducts(params: SearchProductsQueryParams, zoneId?: string) {
    const response = await this.instance.get<FortimeProduct[]>("/wp-json/parhato-api/v1/search-products?", {
      params: {
        q: params.keyword,
        page: params.pageNumber,
        shippingZone: zoneId,
        additionalFilter: params.additionalFeature,
        expressPointId: getCurrentExpressPointId(),
      },
    });

    return response.data.map(convertFortimeProduct);
  }

  async getCustomersChoiceProducts(props: ProductsQueryParams): Promise<ProductResults> {
    const response = await this.instance.get<FortimeProduct[]>("wp-json/wc/v3/products", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        shippingZone: props.zoneId,
        expressPointId: getCurrentExpressPointId(),
        orderby: "popularity",
        order: "desc",
        type: ["variable", "simple"].join(","),
      },
    });

    return {
      products: response.data?.map?.(convertFortimeProduct),
      additionalFeatures: [],
    };
  }

  async getNewestProducts(props: ProductsQueryParams): Promise<ProductResults> {
    const response = await this.instance.get<FortimeProduct[]>("/wp-json/wc/v3/products", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        shippingZone: props.zoneId,
        expressPointId: getCurrentExpressPointId(),
        type: ["variable", "simple"].join(","),
      },
    });
    return {
      products: response.data?.map?.(convertFortimeProduct),
      additionalFeatures: [],
    };
  }

  async getOnSaleProducts(props: ProductsQueryParams): Promise<ProductResults> {
    const response = await this.instance.get<FortimeProduct[]>("/wp-json/wc/v3/products", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        shippingZone: props.zoneId,
        expressPointId: getCurrentExpressPointId(),
        on_sale: true,
        type: ["variable", "simple"].join(","),
      },
    });

    return {
      products: response.data?.map?.(convertFortimeProduct),
      additionalFeatures: [],
    };
  }

  async getFeaturedProducts(props: ProductsQueryParams): Promise<ProductResults> {
    const response = await this.instance.get<FortimeProduct[]>("/wp-json/wc/v3/products", {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        shippingZone: props.zoneId,
        expressPointId: getCurrentExpressPointId(),
        featured: true,
        type: ["variable", "simple"].join(","),
      },
    });

    return {
      products: response.data?.map?.(convertFortimeProduct),
      additionalFeatures: [],
    };
  }

  async getProductById(id: string, zoneId?: string): Promise<Product> {
    const response = await this.instance.get<FortimeProduct>(`/wp-json/wc/v3/products/${id}`, {
      params: {
        ...DEFAULT_PRODUCTS_PARAMS,
        shippingZone: zoneId,
        expressPointId: getCurrentExpressPointId(),
      },
    });

    return convertFortimeProduct(response.data);
  }

  async getIncludeProducts(include: string[], params: { per_page?: string; zoneId?: string }) {
    // const response = await this.instance.get<ParhatoProduct[]>("/wp-json/wc/v3/products", {
    //   params: {
    //     include: include.join(","),
    //     per_page: params.per_page ? params.per_page : include.length + 1,
    //     shippingZone: params.zoneId,
    //     expressPointId: getCurrentExpressPointId(),
    //   },
    // });

    // Temporary crutch
    const productPromises = include.map((productId) => this.getProductById(productId));
    const settledResults = await Promise.allSettled(productPromises);

    const successfulProducts = settledResults
      .filter((result): result is PromiseFulfilledResult<Product> => result.status === "fulfilled")
      .map((result) => result.value);

    return successfulProducts;
  }

  // TODO: make with a proxy layer
  async getProductReviews(id: Product["id"]) {
    const { data: productReviews } = await supabaseClient.functions.invoke<Tables<"product_reviews">[]>(
      "get-product-reviews",
      {
        body: { product_id: id },
      },
    );

    return !!productReviews?.length ? productReviews.map(convertSupaReview) : [];
  }

  // TODO: make with a proxy layer
  async addProductReview(productReviewPayload: ProductReviewPayload): Promise<any> {
    const { data: productReviews } = await supabaseClient.functions.invoke<Tables<"product_reviews">>(
      "add-product-review",
      {
        body: { ...productReviewPayload },
      },
    );

    return !!productReviews ? convertSupaReview(productReviews) : [];
  }

  async addProductSubscription(productId: Product["id"]): Promise<Parhato2ProductSubscription | null> {
    throw new Error("addProductSubscription not implemented");
  }

  async getAllProductsSubscriptions(): Promise<Parhato2ProductSubscription[]> {
    throw new Error("getAllProductsSubscriptions not implemented");
  }
  async getProductsFromParentAndChildCategories(params: ProductsQueryParams): Promise<Product[]> {
    throw new Error("getProductsFromParentAndChildCategories not implemented");
  }

  async getAdditionalProductFilters(): Promise<any[]> {
    throw new Error("getAdditionalProductFilters not implemented");
  }
}

export default ProductsClient;
